import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { 
  Utensils, Trash2, Plus, Calendar, ShoppingCart, ChevronDown, ChevronUp, 
  Clock, Sparkles, Flame, Leaf, Apple, Coffee, Moon, Sun, Zap, ArrowRight,
  Check, Copy, Printer
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import Paywall from "@/components/Paywall";

// Meal icon based on name
function getMealIcon(name) {
  const lower = name?.toLowerCase() || "";
  if (lower.includes("morgen") || lower.includes("breakfast")) return <Coffee className="h-4 w-4 text-amber-400" />;
  if (lower.includes("frokost") || lower.includes("lunch")) return <Sun className="h-4 w-4 text-orange-400" />;
  if (lower.includes("middag") || lower.includes("aften") || lower.includes("dinner")) return <Moon className="h-4 w-4 text-indigo-400" />;
  if (lower.includes("snack") || lower.includes("mellem")) return <Apple className="h-4 w-4 text-red-400" />;
  return <Utensils className="h-4 w-4 text-emerald-400" />;
}

// Feature card for empty state
function FeatureCard({ icon: Icon, title, description, color }) {
  return (
    <div className={`p-5 rounded-xl bg-gradient-to-br ${color} border border-white/5`}>
      <Icon className="h-8 w-8 mb-3 text-white/80" />
      <h3 className="font-bold text-white mb-1">{title}</h3>
      <p className="text-xs text-white/60">{description}</p>
    </div>
  );
}

export default function MealPlans() {
  const { authHeaders } = useAuth();
  const [meals, setMeals] = useState([]);
  const [weeklyMeals, setWeeklyMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedWeekly, setExpandedWeekly] = useState(null);
  const [copiedList, setCopiedList] = useState(false);

  const fetchMeals = async () => {
    try {
      const [dailyRes, weeklyRes] = await Promise.all([
        axios.get(`${API}/meals`, { headers: authHeaders() }),
        axios.get(`${API}/weekly-meals`, { headers: authHeaders() })
      ]);
      setMeals(dailyRes.data);
      setWeeklyMeals(weeklyRes.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchMeals(); }, []);

  const deleteMeal = async (id) => {
    try {
      await axios.delete(`${API}/meals/${id}`, { headers: authHeaders() });
      toast.success("Madplan slettet");
      fetchMeals();
    } catch { toast.error("Fejl ved sletning"); }
  };

  const deleteWeeklyMeal = async (id) => {
    try {
      await axios.delete(`${API}/weekly-meals/${id}`, { headers: authHeaders() });
      toast.success("Ugentlig madplan slettet");
      fetchMeals();
    } catch { toast.error("Fejl ved sletning"); }
  };

  const copyShoppingList = async (list) => {
    const header = "🛒 INDKØBSLISTE - FitrackR\n" + "━".repeat(30) + "\n\n";
    const itemsList = list
      .map(item => `☐ ${item.name} - ${item.amount}`)
      .join("\n");
    const footer = "\n\n" + "━".repeat(30) + "\nGenereret af FitrackR.dk";
    const text = header + itemsList + footer;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopiedList(true);
      toast.success("Indkøbsliste kopieret!");
      setTimeout(() => setCopiedList(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopiedList(true);
      toast.success("Indkøbsliste kopieret!");
      setTimeout(() => setCopiedList(false), 2000);
    }
  };

  const totalMeals = meals.length + weeklyMeals.length;

  return (
    <Paywall>
      <div className="min-h-screen bg-[#09090b]" data-testid="meals-page">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 via-transparent to-transparent" />
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute top-40 right-1/4 w-64 h-64 bg-[#DFFF00]/5 rounded-full blur-3xl" />
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/20 flex items-center justify-center">
                  <Utensils className="h-7 w-7 text-emerald-400" />
                </div>
                <div>
                  <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl sm:text-4xl uppercase tracking-tight text-white">
                    Mad<span className="text-emerald-400">planer</span>
                  </h1>
                  <p className="text-sm text-zinc-400 mt-0.5">
                    {totalMeals > 0 ? `${totalMeals} gemte planer` : "Dine gemte kost- og madplaner"}
                  </p>
                </div>
              </div>
              <Link to="/ai-coach">
                <Button className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:opacity-90 shadow-lg shadow-emerald-500/20 rounded-xl font-bold uppercase tracking-wider h-12 px-6" data-testid="generate-meal-btn">
                  <Sparkles className="mr-2 h-4 w-4" /> Generer med AI
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="bg-[#18181b] border border-white/5 rounded-xl p-1.5 mb-6 w-full md:w-auto">
              <TabsTrigger value="weekly" className="rounded-lg data-[state=active]:bg-emerald-500 data-[state=active]:text-white font-bold uppercase text-xs tracking-wider px-6 py-2.5 transition-all">
                <Calendar className="mr-2 h-4 w-4" /> Ugeplaner 
                <span className="ml-2 bg-white/20 data-[state=active]:bg-white/30 px-2 py-0.5 rounded-md text-[10px]">{weeklyMeals.length}</span>
              </TabsTrigger>
              <TabsTrigger value="daily" className="rounded-lg data-[state=active]:bg-emerald-500 data-[state=active]:text-white font-bold uppercase text-xs tracking-wider px-6 py-2.5 transition-all">
                <Utensils className="mr-2 h-4 w-4" /> Daglige
                <span className="ml-2 bg-white/20 data-[state=active]:bg-white/30 px-2 py-0.5 rounded-md text-[10px]">{meals.length}</span>
              </TabsTrigger>
            </TabsList>

            {/* Weekly Meal Plans */}
            <TabsContent value="weekly">
              {loading ? (
                <div className="space-y-4">
                  {[1,2].map(i => (
                    <div key={i} className="bg-[#18181b] border border-white/5 rounded-2xl h-48 animate-pulse" />
                  ))}
                </div>
              ) : weeklyMeals.length === 0 ? (
                <div className="text-center py-12">
                  {/* Empty state with features */}
                  <div className="max-w-2xl mx-auto">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/20 flex items-center justify-center">
                      <Calendar className="h-10 w-10 text-emerald-400/50" />
                    </div>
                    <h2 style={{ fontFamily: 'Oswald' }} className="text-2xl font-bold uppercase text-white mb-2">
                      Ingen ugentlige madplaner endnu
                    </h2>
                    <p className="text-zinc-400 mb-8 max-w-md mx-auto">
                      Lad AI'en skabe en komplet 7-dages kostplan med opskrifter og indkøbsliste tilpasset dit mål!
                    </p>
                    
                    {/* Feature highlights */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                      <FeatureCard
                        icon={Flame}
                        title="Præcis Kalorie-styring"
                        description="Få madplaner der rammer dit daglige kaloriemål"
                        color="from-orange-500/20 to-orange-500/5"
                      />
                      <FeatureCard
                        icon={ShoppingCart}
                        title="Smart Indkøbsliste"
                        description="Automatisk genereret liste for hele ugen"
                        color="from-blue-500/20 to-blue-500/5"
                      />
                      <FeatureCard
                        icon={Leaf}
                        title="Tilpasset Kost"
                        description="Vegetar, vegan, glutenfri og meget mere"
                        color="from-emerald-500/20 to-emerald-500/5"
                      />
                    </div>
                    
                    <Link to="/ai-coach">
                      <Button className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:opacity-90 shadow-lg shadow-emerald-500/20 rounded-xl font-bold uppercase tracking-wider h-12 px-8">
                        <Sparkles className="mr-2 h-4 w-4" /> Generer din første ugeplan
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {weeklyMeals.map(plan => (
                    <Card key={plan.id} className="bg-[#18181b] border-white/5 rounded-2xl overflow-hidden hover:border-emerald-500/20 transition-all" data-testid={`weekly-meal-${plan.id}`}>
                      <CardHeader className="pb-0">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 flex items-center justify-center">
                              <Calendar className="h-6 w-6 text-emerald-400" />
                            </div>
                            <div>
                              <CardTitle style={{ fontFamily: 'Oswald, sans-serif' }} className="uppercase text-xl tracking-wider text-white">
                                {plan.name}
                              </CardTitle>
                              <div className="flex items-center gap-3 mt-1">
                                <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-lg flex items-center gap-1">
                                  <Sparkles className="h-3 w-3" /> AI-genereret
                                </span>
                                <span className="text-xs text-zinc-500">{new Date(plan.created_at).toLocaleDateString("da-DK")}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button variant="ghost" size="sm" onClick={() => setExpandedWeekly(expandedWeekly === plan.id ? null : plan.id)}
                              className="text-zinc-400 hover:text-white hover:bg-white/5 rounded-lg">
                              {expandedWeekly === plan.id ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteWeeklyMeal(plan.id)} 
                              className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg" data-testid={`delete-weekly-${plan.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardHeader>
                      
                      {/* Stats summary */}
                      {plan.weekly_totals && (
                        <div className="px-6 py-4">
                          <div className="grid grid-cols-4 gap-3">
                            {[
                              { label: "Gns. Kcal", value: plan.weekly_totals.avg_calories, color: "from-[#DFFF00]/20 to-transparent", text: "text-[#DFFF00]" },
                              { label: "Protein", value: `${plan.weekly_totals.avg_protein || 0}g`, color: "from-blue-500/20 to-transparent", text: "text-blue-400" },
                              { label: "Kulhydrat", value: `${plan.weekly_totals.avg_carbs || 0}g`, color: "from-orange-500/20 to-transparent", text: "text-orange-400" },
                              { label: "Fedt", value: `${plan.weekly_totals.avg_fat || 0}g`, color: "from-pink-500/20 to-transparent", text: "text-pink-400" },
                            ].map(({ label, value, color, text }) => (
                              <div key={label} className={`text-center p-3 bg-gradient-to-b ${color} rounded-xl`}>
                                <div style={{ fontFamily: 'JetBrains Mono' }} className={`font-bold text-xl ${text}`}>{value}</div>
                                <div className="text-[10px] uppercase text-zinc-500 mt-0.5">{label}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Day previews (collapsed) */}
                      {expandedWeekly !== plan.id && plan.days?.length > 0 && (
                        <div className="px-6 pb-4">
                          <div className="flex gap-2 overflow-x-auto pb-2">
                            {plan.days.slice(0, 7).map((day, i) => (
                              <div key={i} className="flex-shrink-0 bg-zinc-900/50 rounded-lg px-3 py-2 border border-white/5">
                                <span className="text-xs font-bold text-zinc-400">{day.day?.slice(0, 3)}</span>
                                <p style={{ fontFamily: 'JetBrains Mono' }} className="text-[10px] text-emerald-400 mt-0.5">
                                  {day.total_calories} kcal
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Expanded view */}
                      {expandedWeekly === plan.id && (
                        <CardContent className="pt-0">
                          {/* Days */}
                          <div className="space-y-3 mb-6">
                            {plan.days?.map((day, i) => (
                              <details key={i} className="bg-zinc-900/30 rounded-xl border border-white/5 group">
                                <summary className="p-4 cursor-pointer flex items-center justify-between hover:bg-white/5 rounded-xl transition-colors">
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                                      <span style={{ fontFamily: 'Oswald' }} className="text-sm font-bold text-emerald-400">{i + 1}</span>
                                    </div>
                                    <span style={{ fontFamily: 'Oswald' }} className="font-bold text-white uppercase">{day.day}</span>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span style={{ fontFamily: 'JetBrains Mono' }} className="text-sm bg-emerald-500/10 text-emerald-400 px-3 py-1 rounded-lg">
                                      {day.total_calories} kcal
                                    </span>
                                    <ChevronDown className="h-5 w-5 text-zinc-500 group-open:rotate-180 transition-transform" />
                                  </div>
                                </summary>
                                <div className="px-4 pb-4 space-y-3">
                                  {day.meals?.map((meal, j) => (
                                    <div key={j} className="bg-black/30 rounded-xl p-4 border border-white/5 hover:border-emerald-500/20 transition-colors">
                                      {/* Meal header */}
                                      <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                          {getMealIcon(meal.name)}
                                          <span className="font-bold text-white">{meal.name}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {meal.prep_time && (
                                            <span className="text-[10px] bg-white/5 px-2 py-1 rounded-lg text-zinc-400 flex items-center gap-1">
                                              <Clock className="h-3 w-3" /> {meal.prep_time}
                                            </span>
                                          )}
                                          <span style={{ fontFamily: 'JetBrains Mono' }} className="text-xs bg-[#DFFF00]/10 text-[#DFFF00] px-2 py-1 rounded-lg">
                                            {meal.calories} kcal
                                          </span>
                                        </div>
                                      </div>

                                      {/* Macros row */}
                                      <div className="flex gap-4 mb-3 text-xs">
                                        <span className="text-blue-400">P: {meal.protein || 0}g</span>
                                        <span className="text-orange-400">K: {meal.carbs || 0}g</span>
                                        <span className="text-pink-400">F: {meal.fat || 0}g</span>
                                      </div>

                                      {/* Ingredients */}
                                      {meal.ingredients?.length > 0 && (
                                        <div className="mb-3">
                                          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Ingredienser</p>
                                          <div className="grid grid-cols-2 gap-1.5">
                                            {meal.ingredients.map((ing, k) => (
                                              <div key={k} className="flex justify-between text-xs bg-zinc-900/50 rounded-lg px-3 py-1.5">
                                                <span className="text-zinc-300">{ing.name}</span>
                                                <span style={{ fontFamily: 'JetBrains Mono' }} className="text-emerald-400">{ing.amount}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}

                                      {/* Recipe */}
                                      {meal.recipe && (
                                        <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-xl p-3">
                                          <p className="text-[10px] uppercase tracking-wider text-emerald-400 mb-1.5 flex items-center gap-1">
                                            <Zap className="h-3 w-3" /> Tilberedning
                                          </p>
                                          <p className="text-xs text-zinc-300 leading-relaxed">{meal.recipe}</p>
                                        </div>
                                      )}

                                      {/* Fallback for old format */}
                                      {!meal.ingredients && meal.foods?.length > 0 && (
                                        <div className="space-y-1">
                                          {meal.foods.map((f, k) => (
                                            <div key={k} className="flex justify-between text-xs text-zinc-400">
                                              <span>{f.name} <span className="text-zinc-600">({f.amount})</span></span>
                                              <span style={{ fontFamily: 'JetBrains Mono' }}>{f.calories}kcal</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </details>
                            ))}
                          </div>

                          {/* Shopping list */}
                          {plan.shopping_list?.length > 0 && (
                            <div className="p-5 bg-gradient-to-br from-[#DFFF00]/10 to-transparent rounded-xl border border-[#DFFF00]/20">
                              <div className="flex items-center justify-between mb-4">
                                <h4 style={{ fontFamily: 'Oswald' }} className="uppercase text-sm text-[#DFFF00] flex items-center gap-2">
                                  <ShoppingCart className="h-4 w-4" /> Indkøbsliste ({plan.shopping_list.length} varer)
                                </h4>
                                <Button variant="ghost" size="sm" onClick={() => copyShoppingList(plan.shopping_list)}
                                  className="text-xs text-[#DFFF00] hover:bg-[#DFFF00]/10 rounded-lg">
                                  {copiedList ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                                  {copiedList ? "Kopieret!" : "Kopier"}
                                </Button>
                              </div>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                {plan.shopping_list.map((item, i) => (
                                  <div key={i} className="flex justify-between text-xs bg-black/20 rounded-lg px-3 py-2">
                                    <span className="text-zinc-300">{item.name}</span>
                                    <span style={{ fontFamily: 'JetBrains Mono' }} className="text-[#DFFF00]">{item.amount}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Daily Meal Plans */}
            <TabsContent value="daily">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[1,2].map(i => <div key={i} className="bg-[#18181b] border border-white/5 rounded-2xl h-48 animate-pulse" />)}
                </div>
              ) : meals.length === 0 ? (
                <div className="text-center py-12">
                  <div className="max-w-md mx-auto">
                    <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-500/20 flex items-center justify-center">
                      <Utensils className="h-10 w-10 text-emerald-400/50" />
                    </div>
                    <h2 style={{ fontFamily: 'Oswald' }} className="text-2xl font-bold uppercase text-white mb-2">
                      Ingen daglige madplaner endnu
                    </h2>
                    <p className="text-zinc-400 mb-8">
                      Brug AI Coach til at generere en daglig madplan baseret på dit kaloriemål!
                    </p>
                    <Link to="/ai-coach">
                      <Button className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:opacity-90 rounded-xl font-bold uppercase tracking-wider h-12 px-8">
                        <Sparkles className="mr-2 h-4 w-4" /> Generer madplan
                      </Button>
                    </Link>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {meals.map(meal => (
                    <Card key={meal.id} className="bg-[#18181b] border-white/5 rounded-2xl hover:border-emerald-500/20 transition-all" data-testid={`meal-${meal.id}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle style={{ fontFamily: 'Oswald' }} className="uppercase text-lg tracking-wider text-white">{meal.name}</CardTitle>
                            {meal.ai_generated && (
                              <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded flex items-center gap-1 w-fit mt-1">
                                <Sparkles className="h-2.5 w-2.5" /> AI-genereret
                              </span>
                            )}
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => deleteMeal(meal.id)} 
                            className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg" data-testid={`delete-meal-${meal.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {meal.description && <p className="text-xs text-zinc-400 mb-3">{meal.description}</p>}
                        
                        {/* Macros */}
                        <div className="grid grid-cols-4 gap-2 mb-4">
                          {[
                            { label: "Kcal", value: meal.total_calories, color: "from-[#DFFF00]/20", text: "text-[#DFFF00]" },
                            { label: "Protein", value: `${meal.total_protein}g`, color: "from-blue-500/20", text: "text-blue-400" },
                            { label: "Kulhydrat", value: `${meal.total_carbs}g`, color: "from-orange-500/20", text: "text-orange-400" },
                            { label: "Fedt", value: `${meal.total_fat}g`, color: "from-pink-500/20", text: "text-pink-400" },
                          ].map(({ label, value, color, text }) => (
                            <div key={label} className={`text-center p-2 bg-gradient-to-b ${color} to-transparent rounded-lg`}>
                              <div style={{ fontFamily: 'JetBrains Mono' }} className={`font-bold text-sm ${text}`}>{value}</div>
                              <div className="text-[9px] uppercase text-zinc-500">{label}</div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Meals */}
                        <div className="space-y-2">
                          {meal.meals?.map((m, i) => (
                            <div key={i} className="bg-zinc-900/50 rounded-xl p-3 border border-white/5">
                              <div className="flex items-center gap-2 mb-2">
                                {getMealIcon(m.name)}
                                <span className="text-sm font-bold text-zinc-300">{m.name}</span>
                              </div>
                              <div className="space-y-1 ml-6">
                                {m.foods?.map((f, j) => (
                                  <div key={j} className="flex justify-between text-xs text-zinc-500">
                                    <span>{f.name} <span className="text-zinc-600">({f.amount})</span></span>
                                    <span style={{ fontFamily: 'JetBrains Mono' }} className="text-[#DFFF00]">{f.calories} kcal</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Paywall>
  );
}
